import React, { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled, { css } from 'styled-components';

import {
  LAYOUT,
  LAYOUT_TOP_FILTERS,
  MAP_SIZES,
} from '@rover/constants/src/search/searchPage.constants';
import dataLayer from '@rover/globals/dataLayer';
import { Flex, Heading } from '@rover/kibble/core';
import { TABLET_MAX } from '@rover/kibble/styles/MediaQuery';
import GeoMapStatic from '@rover/react-lib/src/components/GeoMap/GeoMapStatic';
import { MapMetadata } from '@rover/react-lib/src/components/GeoMap/types';
import type { FrontendConfigurationSettings } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettings';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { LatLng } from '@rover/types/src/LatLng';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import SearchMap from '../SearchMap';
import {
  EventTags,
  SearchMapEnlargeOrShrinkEvent,
  SearchMapMovedEvent,
} from '../SearchMap/analyticsEvents';
import { passesThreshold } from '../SearchMap/SearchMap';

import MapControlsV2 from './MapControlsV2';

const DESKTOP = `@media(min-width:${TABLET_MAX + 1}px)`;

const StyledStickyMap = styled(Flex)<{
  isExpanded: boolean;
}>`
  height: 100vh;
  display: none;

  ${DESKTOP.toString()} {
    display: flex;
  }

  ${({ isExpanded }) => {
    const WIDTH_PERCENT = isExpanded ? LAYOUT.WIDE_WIDTH_PERCENT : LAYOUT.NARROW_WIDTH_PERCENT;

    return css`
      width: calc(
        (${LAYOUT_TOP_FILTERS.MAX_CONTENT_WIDTH}px - ${LAYOUT.GUTTER}px) * ${WIDTH_PERCENT}
      );
      flex-shrink: 0;
      border-radius: ${({ theme }) => theme.radii.secondary};
      overflow: hidden;
      margin-left: 8px;
    `;
  }};

  /* IE11 does not have support for '@supports', so it will use the above. The below will override the above if 'sticky' is supported. */
  @supports (position: sticky) {
    position: sticky;
    top: 0;
    right: 0;
  }

  background-color: ${({ theme }) => theme.colors.neutral['100']};
`;

type Props = {
  onMarkerClick: (index: number) => void;
  isExpanded: boolean;
  toggleIsExpanded: () => void;
  fireSearch: (arg0: { searchInitiatedByMap: boolean }) => void;
  updateFilters: (searchFilters: Partial<SearchFilters>) => void;
  isSearchLoading: boolean;
  center?: LatLng | null;
  zoomLevel?: number | null;
  baseTags: EventTags;
  searchResults: SearchResult[];
  showDynamicMap: boolean;
  settings: FrontendConfigurationSettings;
};

const DESKTOP_MEDIA_QUERY = `(min-width:${TABLET_MAX + 1}px)`;

const StickyMap = ({
  onMarkerClick,
  isExpanded,
  toggleIsExpanded,
  fireSearch,
  updateFilters,
  isSearchLoading,
  center,
  zoomLevel,
  baseTags,
  searchResults,
  showDynamicMap,
  settings,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const [mapHasMoved, setMapHasMoved] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    setIsDesktop(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent): void => setIsDesktop(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const handleSearchThisAreaPress = (): void => {
    setMapHasMoved(false);
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    window.scrollTo({ top: 0, behavior: 'smooth' });
    updateFilters({ location: t`Map area` });
    fireSearch({
      searchInitiatedByMap: true,
    });
  };

  const handleMapExpanderClick = (): void => {
    const nextIsExpanded = !isExpanded;
    emitAnalyticsEvent(
      new SearchMapEnlargeOrShrinkEvent({
        ...baseTags,
        action: nextIsExpanded ? 'enlarge' : 'shrink',
      })
    );

    if (toggleIsExpanded) {
      toggleIsExpanded();
    }

    const newSize = nextIsExpanded ? MAP_SIZES.LARGE : MAP_SIZES.SMALL;
    dataLayer?.push({
      event: 'search-map-size-changed',
      mapSize: newSize,
    });
  };

  return (
    <StyledStickyMap
      flexDirection="column"
      flexWrap="nowrap"
      flex="none"
      isExpanded={isExpanded}
      data-qa-id="container-map-search"
    >
      <Heading webProps={{ as: 'h2', a11yHidden: true }}>Search Map</Heading>
      <GeoMapStatic
        visible={!showDynamicMap}
        results={searchResults}
        staticGoogleMapsApiKey={settings.staticGoogleMapsApiKey}
        center={center}
        zoom={zoomLevel}
      />
      {isDesktop && showDynamicMap && (
        <SearchMap
          onMarkerClick={onMarkerClick}
          onMapMoved={(mapMetadata) => {
            if (!center || !zoomLevel) return;

            const passes = passesThreshold(
              {
                centerlat: typeof center.lat === 'string' ? parseFloat(center.lat) : center.lat,
                centerlng: typeof center.lng === 'string' ? parseFloat(center.lng) : center.lng,
                zoomlevel: zoomLevel,
              } as MapMetadata,
              mapMetadata
            );
            if (passes) {
              emitAnalyticsEvent(new SearchMapMovedEvent(baseTags));
              updateFilters(mapMetadata);
              setMapHasMoved(true);
            }
          }}
          onZoomChanged={(mapMetadata) => {
            updateFilters(mapMetadata);
          }}
          options={{
            zoomControl: false,
          }}
        >
          <MapControlsV2
            isExpanded={isExpanded}
            onMapExpanderClick={handleMapExpanderClick}
            mapHasMoved={mapHasMoved}
            onSearchThisAreaPress={handleSearchThisAreaPress}
            isSearchLoading={isSearchLoading}
          />
        </SearchMap>
      )}
    </StyledStickyMap>
  );
};

export default StickyMap;
